import { CircleCheckFilledIcon, EditIcon, TrashIcon, WarningFilledIcon } from "@xola/icons";
import { Alert, flash, getSymbol, Table } from "@xola/ui-kit";
import { DurationUnit, SecondaryButton } from "common";
import { noop } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { AmountType, FieldType, PickupCatalogItem } from "types";
import { CreatePickupLocationModal } from "./CreatePickupLocationModal";
import { api } from "@/api/api";
import { PromptModal } from "@/components/Common/PromptModal";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { useForms } from "@/hooks/resources/useForms";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";

type PickupLocationItemProps = {
    pickupLocation: PickupCatalogItem;
};

export const PickupLocationItem = ({ pickupLocation }: PickupLocationItemProps) => {
    const { data: forms } = useForms();
    const invalidate = useInvalidate();

    const confirmDeleteModal = useModal();
    const editPickupLocationModal = useModal();

    const seller = useSeller();

    const currency = seller?.currency;

    const { mutateAsync: deletePickupLocation, isLoading: isDeleting } = useMutation(async ({ id }: { id: ID }) => {
        const url = `/catalogItems/${id}?cascade=true`;

        return api.delete(url);
    });

    const pickupFields = forms.flatMap((form) => form.fields).filter((field) => field?.type === FieldType.PICKUP);
    const warningCount = pickupFields.filter((field) =>
        field.options?.some((opt: any) => opt?.pickupLocation?.id === pickupLocation.id),
    ).length;

    const handleDeletePickupLocation = async () => {
        try {
            await deletePickupLocation({ id: pickupLocation.id });
            confirmDeleteModal.hide();
            invalidate("catalogItems", pickupLocation?.id);
            flash.show({ text: `Pickup Location removed`, color: "success" });
        } catch (error: any) {
            const deleteError = error?.response?.data?.field ?? {};
            flash.show({ text: deleteError?.message ?? "Error in deleting", color: "danger" });
        }
    };

    return (
        <>
            <Table.Row key={pickupLocation.id} data-testid="pickup-location-row" className="justify-between">
                <Table.Cell className="no-translate w-1/4">{pickupLocation.name}</Table.Cell>

                <Table.Cell data-testid="pickup-location-price-per-person-cell" className="no-translate w-1/4">
                    {pickupLocation.prices?.perPerson &&
                        `${
                            pickupLocation.prices.perPerson.priceType === AmountType.ABSOLUTE ? getSymbol(currency) : ""
                        } ${pickupLocation.prices.perPerson.price}${
                            pickupLocation.prices.perPerson.priceType === AmountType.PERCENT ? " %" : ""
                        }`}
                </Table.Cell>

                <Table.Cell data-testid="pickup-location-price-per-group-cell" className="no-translate w-1/4">
                    {pickupLocation.prices?.perGroup &&
                        `${
                            pickupLocation.prices.perGroup.priceType === AmountType.ABSOLUTE ? getSymbol(currency) : ""
                        } ${pickupLocation.prices.perGroup.price}${
                            pickupLocation.prices.perGroup.priceType === AmountType.PERCENT ? " %" : ""
                        }`}
                </Table.Cell>

                <Table.Cell data-testid="pickup-location-timing-cell">
                    {Number(pickupLocation.arrivalOffset) > 0 && (
                        <span>
                            -
                            <TimePickerPopover
                                disabled
                                showInHours
                                submitOnApply={false}
                                preferredUnit={DurationUnit.MINUTE}
                                value={pickupLocation.arrivalOffset ?? 0}
                                onApply={noop}
                            />
                        </span>
                    )}
                </Table.Cell>

                <Table.Cell className="space-x-1">
                    <SecondaryButton
                        size="tiny"
                        data-testid="pickup-location-edit-button"
                        onClick={editPickupLocationModal.toggle}
                    >
                        <EditIcon />
                    </SecondaryButton>
                    <SecondaryButton
                        data-testid="pickup-location-delete-button"
                        size="tiny"
                        onClick={confirmDeleteModal.toggle}
                    >
                        <TrashIcon />
                    </SecondaryButton>
                </Table.Cell>
            </Table.Row>

            <CreatePickupLocationModal
                pickupLocation={pickupLocation}
                isOpen={editPickupLocationModal.isVisible}
                onClose={editPickupLocationModal.toggle}
            />

            <PromptModal
                title="Delete Pickup Location?"
                titleDataTestId="delete-pickup-location-title"
                isOpen={confirmDeleteModal.isVisible || isDeleting}
                dialogDataTestIds={{
                    submitTestId: "delete-pickup-location-confirm-button",
                    secondaryTestId: "delete-pickup-location-cancel-button",
                }}
                onConfirm={handleDeletePickupLocation}
                onClose={confirmDeleteModal.hide}
            >
                <div className="flex flex-col gap-4">
                    <span>
                        Please confirm you would like to delete the <strong>{pickupLocation.name}</strong> pickup
                        location.
                    </span>

                    {warningCount === 0 ? (
                        <Alert color="success" data-testid="delete-pickup-location-safe">
                            <div className="flex items-center gap-4">
                                <span className="w-5">
                                    <CircleCheckFilledIcon className="text-success" size="large" />
                                </span>
                                <span>This pickup location is not in use by any products and is safe to delete</span>
                            </div>
                        </Alert>
                    ) : (
                        <Alert color="warning" data-testid="delete-pickup-location-warning">
                            <div className="flex items-center gap-4">
                                <span className="w-5">
                                    <WarningFilledIcon className="text-yellow" size="large" color="warning" />
                                </span>
                                <span>
                                    This pickup location is currently in use by
                                    <span className="font-bold underline"> {warningCount} questionnaires</span>.
                                    Performing this action will remove this pickup location as an option from these
                                    products
                                </span>
                            </div>
                        </Alert>
                    )}
                </div>
            </PromptModal>
        </>
    );
};
